import { createContext, useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { format, formatDistanceToNow, fromUnixTime } from "date-fns";
import {
  CheckIcon,
  CircleCheckIcon,
  CopyIcon,
  LoaderCircleIcon,
  PaperclipIcon,
} from "lucide-react";

import { AnimatedModal } from "@/src/components/AnimatedModal";
import { Avatar } from "@/src/components/account/Avatar";
import { Button } from "@/src/components/tailwind/Button";
import { FilePill } from "@/src/components/FilePill";
import { Pills } from "@/src/components/Pills";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import { AddFileModal } from "@/src/components/AddFileModal";
import {
  AnswerEditor,
  AnswerViewer,
} from "@/src/components/answer_editor/AnswerEditor";
import {
  getQuestionPriorityText,
  QuestionPriorityIcon,
} from "@/src/components/QuestionPriorityIcon";
import {
  AnswerReference,
  ClusterQuestionFragment,
  DataRoomFile,
  PermissionsResourceType,
  QuestionClusterStatus,
  useCreateQuestionAnswerMutation,
  useNextQuestionClusterQuery,
  useQuestionClusterQuery,
  useUpdateQuestionClusterMutation,
  useUpdateQuestionClusterMemberMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useQueryClient } from "@tanstack/react-query";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

type ClusterMember = {
  id: string;
  question: ClusterQuestionFragment;
};

// ---------------------------------------------------------------------------
// Context — shared answer state for the input
// ---------------------------------------------------------------------------

type ClusterAnswerContextType = {
  answer: string;
  setAnswer: (answer: string) => void;
  answerFiles: AnswerReference[];
  setAnswerFiles: (files: AnswerReference[]) => void;
};

const ClusterAnswerContext = createContext<ClusterAnswerContextType>({
  answer: "",
  setAnswer: () => { },
  answerFiles: [],
  setAnswerFiles: () => { },
});

// ---------------------------------------------------------------------------
// Public modal — routes to specific or sequential content based on clusterId
// ---------------------------------------------------------------------------

export function ClusterModal(props: {
  open: boolean;
  onClose: () => void;
  clusterId?: string;
}) {
  return (
    <AnimatedModal
      padding="0"
      size="full"
      open={props.open}
      onClose={props.onClose}
      closeOnOutsideClick={false}
    >
      <AnimatedModal.Header
        title="Similar questions"
        onClose={props.onClose}
      />
      <div className="rounded-b-lg">
        {props.clusterId ? (
          <SpecificClusterContent
            clusterId={props.clusterId}
            onClose={props.onClose}
          />
        ) : (
          <SequentialClusterContent onClose={props.onClose} />
        )}
      </div>
    </AnimatedModal>
  );
}

// ---------------------------------------------------------------------------
// Specific cluster — loads by ID, "Done" footer
// ---------------------------------------------------------------------------

function SpecificClusterContent(props: {
  clusterId: string;
  onClose: () => void;
}) {
  const client = useGqlClient();
  const [answer, setAnswer] = useState("");
  const [answerFiles, setAnswerFiles] = useState<AnswerReference[]>([]);

  const clusterQuery = useQuestionClusterQuery(client, { id: props.clusterId });

  if (clusterQuery.isLoading || !clusterQuery.data) {
    return <ModalLoading />;
  }

  if (clusterQuery.error) {
    return (
      <div className="p-6">
        <p className="text-persian-990">Something went wrong</p>
      </div>
    );
  }

  const { members } = clusterQuery.data.questionCluster;

  return (
    <ClusterAnswerContext.Provider
      value={{ answer, setAnswer, answerFiles, setAnswerFiles }}
    >
      <div className="flex flex-col h-[calc(100vh-200px)]">
        <div className="flex-1 min-h-0">
          <ClusterLayout
            members={members}
            clusterId={props.clusterId}
            onAnswerPosted={() => clusterQuery.refetch()}
          />
        </div>
        <div className="border-t border-concrete-100 shrink-0 p-3 flex items-center justify-end">
          <Button variant="positive" text="Done" onClick={props.onClose} />
        </div>
      </div>
    </ClusterAnswerContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Sequential cluster — loads next in queue, Skip/Dismiss/Resolve footer
// ---------------------------------------------------------------------------

function SequentialClusterContent(props: { onClose: () => void }) {
  const { dealId } = useParams<{ dealId: string }>();
  const client = useGqlClient();
  const queryClient = useQueryClient();

  const [answer, setAnswer] = useState("");
  const [answerFiles, setAnswerFiles] = useState<AnswerReference[]>([]);
  const [actionState, setActionState] = useState<"" | "dismissing" | "loading-next">("");

  const nextClusterQuery = useNextQuestionClusterQuery(client, {
    dealID: dealId,
  });
  const updateCluster = useUpdateQuestionClusterMutation(client);

  function handleDismiss() {
    const cluster = nextClusterQuery.data?.nextQuestionCluster;
    if (!cluster) return;
    setActionState("dismissing");
    updateCluster.mutate(
      { input: { id: cluster.id, status: QuestionClusterStatus.Dismissed } },
      {
        onSuccess: () => {
          setTimeout(() => {
            setAnswer("");
            setAnswerFiles([]);
            nextClusterQuery.refetch();
            setActionState("");
          }, 1000);
        },
      },
    );
  }

  function handleLoadNext() {
    setAnswer("");
    setAnswerFiles([]);
    setActionState("loading-next");
    nextClusterQuery.refetch().then(() => setActionState(""));
  }

  // After answers are posted, optimistically mark all cluster members as answered
  // in the cache so the UI reflects the new state without jumping to the next cluster.
  function handleAnswerPosted() {
    queryClient.setQueryData(
      ["NextQuestionCluster", { dealID: dealId }],
      (old: any) => {
        if (!old?.nextQuestionCluster) return old;
        return {
          ...old,
          nextQuestionCluster: {
            ...old.nextQuestionCluster,
            members: old.nextQuestionCluster.members.map((m: any) => ({
              ...m,
              question: {
                ...m.question,
                answeredAt: m.question.answeredAt ?? Math.floor(Date.now() / 1000),
              },
            })),
          },
        };
      },
    );
  }

  if (nextClusterQuery.error) {
    return (
      <div className="p-6">
        <p className="text-persian-990">Something went wrong</p>
      </div>
    );
  }

  if (nextClusterQuery.isLoading || !nextClusterQuery.data) {
    return <ModalLoading />;
  }

  if (!nextClusterQuery.data.nextQuestionCluster) {
    return (
      <div className="w-full h-96 flex flex-col items-center justify-center">
        <CircleCheckIcon className="w-24 h-24 text-persian-990" />
        <p className="text-lg mt-2 font-semibold text-persian-990">
          All done for now!
        </p>
        <p className="text-sm text-persian-990">
          Liquid has not detected any other similar question groups.
        </p>
      </div>
    );
  }

  const cluster = nextClusterQuery.data.nextQuestionCluster;
  const allAnswered = cluster.members.every((m) => !!m.question.answeredAt);

  return (
    <ClusterAnswerContext.Provider
      value={{ answer, setAnswer, answerFiles, setAnswerFiles }}
    >
      <div className="flex flex-col h-[calc(100vh-200px)]">
        <div className="flex-1 min-h-0">
          {actionState === "dismissing" ? (
            <div className="h-full flex items-center justify-center">
              <UpdatingState state="dismissing" />
            </div>
          ) : actionState === "loading-next" ? (
            <ModalLoading />
          ) : (
            <ClusterLayout
              members={cluster.members}
              clusterId={cluster.id}
              onAnswerPosted={handleAnswerPosted}
            />
          )}
        </div>

        <div className="border-t border-concrete-100 shrink-0 p-3 flex items-center justify-between gap-x-2">
          <Button
            variant="neutral"
            text="Dismiss"
            isDisabled={actionState !== ""}
            isLoading={actionState === "dismissing"}
            loadingText="Dismissing..."
            onClick={handleDismiss}
          />
          <Button
            isDisabled={!allAnswered || actionState !== ""}
            variant="positive"
            text="Load next group"
            isLoading={actionState === "loading-next"}
            loadingText="Loading..."
            onClick={handleLoadNext}
          />
        </div>
      </div>
    </ClusterAnswerContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Shared 2-column layout
// ---------------------------------------------------------------------------

function ClusterLayout(props: {
  members: ClusterMember[];
  clusterId: string;
  onAnswerPosted: () => void;
}) {
  // Sort unanswered first
  const members = [...props.members].sort((a, b) => {
    const aAnswered = !!a.question.answeredAt;
    const bAnswered = !!b.question.answeredAt;
    if (!aAnswered && bAnswered) return -1;
    if (aAnswered && !bAnswered) return 1;
    return 0;
  });

  const unansweredMembers = members.filter((m) => !m.question.answeredAt);
  const allAnswered = unansweredMembers.length === 0;

  const membersToAnswer = unansweredMembers.length > 0 ? unansweredMembers : members;

  return (
    <div className="flex divide-x divide-concrete-100 h-full">
      {/* Left: banner (when all answered) + questions list — scrollable */}
      <div className="w-1/2 overflow-y-auto p-6 space-y-5">
        {allAnswered && (
          <div className="flex items-center gap-x-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2.5">
            <CheckIcon className="w-4 h-4 text-green-600 shrink-0" />
            <p className="text-sm font-medium text-green-800">
              All questions in this group have been answered.
            </p>
          </div>
        )}
        {members.map((member) => (
          <QuestionItem
            key={member.id}
            memberId={member.id}
            question={member.question}
            onRemoved={props.onAnswerPosted}
          />
        ))}
      </div>

      {/* Right: scrollable answers above, pinned input below — always shown */}
      <div className="w-1/2 flex flex-col">
        <div className="flex-1 overflow-y-auto p-6">
          <AnswerCards members={members} />
        </div>
        <div className="shrink-0 border-t border-concrete-100 p-4">
          <ClusterAnswerInput
            unansweredMembers={membersToAnswer}
            clusterId={props.clusterId}
            onAnswerPosted={props.onAnswerPosted}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left column — question metadata only
// ---------------------------------------------------------------------------

function QuestionItem(props: {
  memberId: string;
  question: ClusterQuestionFragment;
  onRemoved: () => void;
}) {
  const { question } = props;
  const answered = !!question.answeredAt;
  const client = useGqlClient();
  const updateMember = useUpdateQuestionClusterMemberMutation(client);

  function handleRemove() {
    updateMember.mutate(
      { input: { id: props.memberId, remove: true } },
      { onSuccess: props.onRemoved },
    );
  }

  return (
    <div className="border border-concrete-100 rounded-lg p-4">
      <div className="flex items-start justify-between gap-x-2">
        <div className="min-w-0">
          <QuestionBreadcrumbs question={question} />
          <p className="text-sm font-semibold text-concrete-950">
            {question.title}
          </p>
          {question.body && (
            <p className="text-sm text-concrete-600 mt-1">{question.body}</p>
          )}
        </div>

        <div className="shrink-0 flex items-center gap-x-1.5">
          {answered ? (
            <span className="inline-flex items-center gap-x-1 rounded-full bg-green-50 border border-green-200 px-2 py-0.5 text-xs font-medium text-green-700">
              <CheckIcon className="w-3 h-3" />
              Answered
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-persian-50 border border-persian-200 px-2 py-0.5 text-xs font-medium text-persian-700">
              Unanswered
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-x-2 flex-wrap">
          <AskedByBadge question={question} />
          <PriorityBadge question={question} />
          <span className="text-xs text-concrete-400">
            Asked {format(fromUnixTime(question.createdAt), "d MMM")}
          </span>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={updateMember.isPending}
          className="text-xs text-concrete-400 hover:text-red-400 transition-colors shrink-0 ml-2"
        >
          Remove from group
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right column — all answers from all members + shared answer input
// ---------------------------------------------------------------------------

type ClusterAnswer = NonNullable<
  NonNullable<ClusterQuestionFragment["answers"]>[number]
>;

type AnswerGroup = {
  answer: ClusterAnswer;
  usedForCount: number;
};

function AnswerCards(props: { members: ClusterMember[] }) {
  const allAnswers = props.members
    .flatMap((member) =>
      (member.question.answers ?? []).filter(
        (a): a is NonNullable<typeof a> => a !== null,
      ),
    )
    .sort((a, b) => a.createdAt - b.createdAt);

  // Group answers with identical text — keep the first occurrence, track count
  const grouped: AnswerGroup[] = [];
  for (const answer of allAnswers) {
    const existing = grouped.find(
      (g) => g.answer.answer.trim() === answer.answer.trim(),
    );
    if (existing) {
      existing.usedForCount++;
    } else {
      grouped.push({ answer, usedForCount: 1 });
    }
  }

  if (grouped.length === 0) {
    return (
      <p className="text-sm text-concrete-400">No answers yet</p>
    );
  }

  return (
    <div className="flex flex-col gap-y-3">
      {grouped.map((group) => (
        <AnswerCard
          key={group.answer.id}
          answer={group.answer}
          usedForCount={group.usedForCount}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single answer card with attribution
// ---------------------------------------------------------------------------

function AnswerCard(props: {
  answer: ClusterAnswer;
  usedForCount: number;
}) {
  const { answer } = props;
  const { setAnswer, setAnswerFiles } = useContext(ClusterAnswerContext);

  return (
    <div>
      <div className="bg-white border border-concrete-100 rounded-md p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-x-2">
            <Avatar account={answer.answeredBy} size="s" />
            <p className="font-semibold text-concrete-950 text-sm">
              {answer.answeredBy?.name}
            </p>
            <p className="text-xs text-concrete-400">
              {formatDistanceToNow(fromUnixTime(answer.createdAt), {
                addSuffix: true,
              })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setAnswer(answer.answer);
              setAnswerFiles(answer.answerFiles as AnswerReference[]);
            }}
            className="flex items-center gap-x-1.5 hover:bg-concrete-100 px-2 py-1 rounded-md text-concrete-600 hover:text-concrete-950 transition-colors"
          >
            <CopyIcon className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Copy answer</span>
          </button>
        </div>

        {props.usedForCount > 1 && (
          <p className="text-xs text-concrete-400 mt-1">
            Used as answer for{" "}
            <span className="font-medium text-concrete-600">
              {props.usedForCount} questions
            </span>{" "}
            in this group
          </p>
        )}

        <div className="text-sm text-concrete-950 mt-2 py-1">
          <AnswerViewer content={answer.answer} />
        </div>

        {answer.answerFiles && answer.answerFiles.length > 0 && (
          <div className="pt-1">
            <Pills>
              {answer.answerFiles.map((fileRef) => (
                <FilePill
                  key={fileRef.file.id + (fileRef.pageIndex ?? "")}
                  id={fileRef.file.id}
                  name={fileRef.file.name}
                  type={fileRef.file.fileType}
                  pageIndex={fileRef.pageIndex ?? undefined}
                  rectsOnPage={fileRef.rectsOnPage}
                  useSlideOver={true}
                  showDetailsCard={false}
                />
              ))}
            </Pills>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared answer input — posts to all unanswered members
// ---------------------------------------------------------------------------

function ClusterAnswerInput(props: {
  unansweredMembers: ClusterMember[];
  clusterId: string;
  onAnswerPosted: () => void;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const createAnswer = useCreateQuestionAnswerMutation(client);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openFileModal, setOpenFileModal] = useState(false);

  const { answer, setAnswer, answerFiles, setAnswerFiles } =
    useContext(ClusterAnswerContext);

  const firstUnanswered = props.unansweredMembers[0];
  const count = props.unansweredMembers.length;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!answer.trim()) {
      setError("Your answer cannot be empty");
      return;
    }
    setError(null);
    setIsSubmitting(true);

    try {
      await Promise.all(
        props.unansweredMembers.map((member) =>
          createAnswer.mutateAsync({
            questionId: member.question.id,
            answer,
            dataRoomFileIds: answerFiles.map((f) => f.file.id),
            answerFiles: answerFiles.map((f) => ({
              dataRoomFileID: f.file.id,
              pageIndex: f.pageIndex,
              rectsOnPage: f.rectsOnPage,
            })),
          }),
        ),
      );

      queryClient.invalidateQueries({
        queryKey: ["QuestionCluster", { id: props.clusterId }],
      });
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === "QuestionClusters",
      });

      setAnswer("");
      setAnswerFiles([]);
      props.onAnswerPosted();
    } catch {
      setError("Something went wrong posting your answer");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <PermissionCheck
      input={{
        resource: {
          resourceID: firstUnanswered.question.id,
          resourceType: PermissionsResourceType.Question,
        },
        permissionKey: "questions:answer",
      }}
    >
      <form onSubmit={handleSubmit}>
        <div className="rounded-lg shadow-sm ring-1 ring-inset ring-concrete-100 focus-within:ring-persian-600 focus-within:ring-1">
          <AnswerEditor
            content={answer}
            onChange={(md) => {
              setAnswer(md);
              if (error) setError(null);
            }}
            placeholder="Write an answer..."
          />
          {error && (
            <div className="px-4 pb-2 text-red-500 text-sm">{error}</div>
          )}
          <div className="mx-2 h-px bg-concrete-100" />
          <div className="p-2 flex items-center justify-between gap-x-2">
            <div className="flex items-center flex-wrap gap-2">
              {answerFiles.map((file) => (
                <FilePill
                  key={file.file.id}
                  id={file.file.id}
                  name={file.file.name}
                  type={file.file.fileType}
                  pageIndex={file.pageIndex ?? undefined}
                  rectsOnPage={file.rectsOnPage}
                  useSlideOver={true}
                  showDetailsCard={false}
                  onRemove={() =>
                    setAnswerFiles(
                      answerFiles.filter((f) => f.file.id !== file.file.id),
                    )
                  }
                />
              ))}
              <Button
                text="Attach document"
                variant="secondary"
                size="s"
                icon={PaperclipIcon}
                onClick={() => setOpenFileModal(true)}
                type="button"
              />
            </div>
            <Button
              type="submit"
              size="s"
              variant="positive"
              isLoading={isSubmitting}
              loadingText="Posting..."
              isDisabled={answer.trim() === ""}
              text={
                count > 1
                  ? `Post answer to all ${count} questions`
                  : "Post answer"
              }
            />
          </div>
        </div>
      </form>

      <AddFileModal
        open={openFileModal}
        onClose={() => setOpenFileModal(false)}
        onFilesSelected={(files) => {
          const toAdd = files
            .filter((f) => !answerFiles.find((a) => a.file.id === f.file.id))
            .map((f) => ({
              file: f.file as DataRoomFile,
              pageIndex: 0,
              rectsOnPage: [],
              quote: "",
              topReference: true,
            }));
          setAnswerFiles([...answerFiles, ...toAdd]);
        }}
      />
    </PermissionCheck>
  );
}

// ---------------------------------------------------------------------------
// Badges & breadcrumbs
// ---------------------------------------------------------------------------

function AskedByBadge(props: { question: ClusterQuestionFragment }) {
  return (
    <div className="flex items-center gap-x-1.5">
      <Avatar account={props.question.createdBy} size="xs" />
      <p className="text-xs text-concrete-500">
        {props.question.createdBy.name}
      </p>
    </div>
  );
}

function PriorityBadge(props: { question: ClusterQuestionFragment }) {
  return (
    <div className="flex items-center gap-x-1">
      <QuestionPriorityIcon priority={props.question.priority} />
      <p className="text-xs text-concrete-500">
        {getQuestionPriorityText(props.question.priority)}
      </p>
    </div>
  );
}

function QuestionBreadcrumbs(props: { question: ClusterQuestionFragment }) {
  const nodes: string[] = [];

  if (props.question.ddql) {
    props.question.ddql.parentDdqls.forEach((d) => {
      if (d.name) nodes.push(d.name);
    });
    nodes.push(props.question.ddql.name);
  }

  if (!nodes.length) return null;

  return (
    <div className="flex items-center gap-x-1 mb-1">
      {nodes.map((crumb, i) => (
        <span key={i} className="text-xs text-concrete-400">
          {crumb}
          {i < nodes.length - 1 && (
            <span className="mx-1 font-light">/</span>
          )}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading / transition states
// ---------------------------------------------------------------------------

function UpdatingState(props: { state: "dismissing" }) {
  return (
    <div className="flex items-center gap-x-3">
      <LoaderCircleIcon className="w-12 h-12 animate-spin text-concrete-950" />
      <p className="text-lg font-semibold text-persian-990">Dismissing...</p>
    </div>
  );
}

function ModalLoading() {
  return (
    <div className="flex flex-col h-[calc(100vh-200px)]">
      <div className="flex divide-x divide-concrete-100 flex-1 min-h-0">
        {[1, 2].map((i) => (
          <div key={i} className="flex-1 animate-pulse p-6 space-y-4 overflow-hidden">
            <div className="h-20 bg-gray-100 rounded-lg" />
            <div className="h-20 bg-gray-100 rounded-lg" />
            <div className="h-4 bg-gray-100 rounded w-2/3" />
            <div className="h-32 bg-gray-100 rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}
